<template>
  <div class="risk-table-section">
    <h4 class="table-title">{{ formatMetric(title) }}</h4>
    <div class="table-wrapper">
      <table class="data-table">
        <thead>
          <tr>
            <th v-for="(header, index) in headers" :key="index" :style="getColumnStyle(header)">
              {{ formatTitle(header) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, rowIndex) in tableData" :key="rowIndex">
            <td v-for="(value, key) in row" :key="key" :class="getCellClass(key, value, row)" :style="getColumnStyle(key)">
              {{ formatCellValue(value) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RiskRatingTable',
  props: {
    title: {
      type: String,
      required: true
    },
    tableData: {
      type: Array,
      required: true
    }
  },
  computed: {
    headers() {
      if (!this.tableData || this.tableData.length === 0) return []
      return Object.keys(this.tableData[0])
    },
    
    columnWidths() {
      // Calculate consistent column widths based on number of columns
      const numColumns = this.headers.length
      if (numColumns === 0) return {}
      
      const widths = {}
      
      // First column always gets 40% of total width
      if (numColumns > 0) {
        widths[this.headers[0]] = '40%'
      }
      
      // Remaining columns share the rest (60%) proportionally
      if (numColumns > 1) {
        const remainingWidth = 60 / (numColumns - 1)
        for (let i = 1; i < numColumns; i++) {
          widths[this.headers[i]] = `${remainingWidth}%`
        }
      }
      
      return widths
    }
  },
  methods: {
    formatTitle(title) {
      if (title === 'Obtained_Score' || title === 'Current_Value') {
        return title.split('_')[1]
      }
      if (title === 'Total_Score') {
        return 'Max Score'
      }
      return this.formatMetric(title)
    },
    formatMetric(title) {
      // Replace all occurrences of underscore
      return title.replace(/_/g, ' ')
    },
    
    formatCellValue(value) {
      if (value === null || value === undefined || value === '') return '-'
      if (typeof value === 'number') {
        if (Number.isInteger(value)) return value.toLocaleString()
        return value.toFixed(2)
      }
      return this.formatMetric(String(value))
    },
    
    getCellClass(key, value, row) {
      const classes = []
      
      // Color code score cells based on percentage
      if (key.includes('Score') || key.includes('Percentage')) {
        classes.push('score-cell')
        
        // Calculate percentage for Obtained_Score
        if (key === 'Obtained_Score' || key === 'Obtained Score') {
          const totalScore = row['Total_Score'] || row['Total Score'] || row['Max_Score'] || row['Max Score']
          if (totalScore && typeof value === 'number') {
            const percentage = value / parseFloat(totalScore)
            classes.push(this.getRiskColorClass(percentage))
          }
        }
        
        // Calculate percentage for Percentage column
        if (key === 'Percentage' && typeof value === 'number') {
          classes.push(this.getRiskColorClass(value))
        }
      }
      
      if (key.includes('Risk') && typeof value === 'string' && value.includes('Risk')) {
        classes.push('risk-header-cell')
      }
      
      return classes.join(' ')
    },
    
    getRiskColorClass(percentage) {
      if (percentage >= 0.8) return 'risk-low'
      if (percentage >= 0.6) return 'risk-medium'
      return 'risk-high'
    },
    
    getColumnStyle(columnKey) {
      return {
        width: this.columnWidths[columnKey] || 'auto',
        minWidth: '100px'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../index.scss';

.risk-table-section {
  margin-bottom: 0;
}

.table-title {
  font-size: 1rem;
  font-weight: 600;
  color: #6c757d;
  margin: 0 0 0.75rem 0;
  font-family: $brand-font-medium;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.table-wrapper {
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 1rem;
  table-layout: fixed;
  
  thead {
    background: #f8f9fa;
    
    th {
      padding: 0.5rem 0.75rem;
      text-align: left;
      font-weight: 600;
      color: #2d3748;
      border-bottom: 1px solid #e2e8f0;
      font-family: $brand-font-medium;
      white-space: nowrap;
      font-size: 1rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  
  tbody {
    tr {
      border-bottom: 1px solid #f1f3f5;
      
      &:hover {
        background: #f8f9fa;
      }
      
      &:last-child {
        border-bottom: none;
      }
    }
    
    td {
      padding: 0.5rem 0.75rem;
      color: #495057;
      font-family: $brand-font-regular;
      font-size: 1rem;
      overflow: hidden;
      text-overflow: ellipsis;
      word-wrap: break-word;
      
      &.score-cell {
        font-weight: 600;
        
        &.risk-low {
          color: #10b981;
        }
        
        &.risk-medium {
          color: #f59e0b;
        }
        
        &.risk-high {
          color: #ef4444;
        }
        
        // Default color if no risk class
        &:not(.risk-low):not(.risk-medium):not(.risk-high) {
          color: $color-primary;
        }
      }
      
      &.risk-header-cell {
        font-weight: 600;
        color: #2d3748;
      }
    }
  }
}

@media (max-width: 768px) {
  .data-table {
    font-size: 1rem;
    
    th, td {
      padding: 0.5rem;
    }
  }
}
</style>

